import { useEffect, useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useGroupCount, groupTabLabel } from '../lib/groups';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { useFlag, FEATURE } from '../lib/useFlag';
import { useUserNames } from '../lib/useUserNames';
import { downloadDocument } from '../lib/downloadDocument';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { Badge, Button, Select, SearchableSelect, IconArchive } from '../atoms';
import type { SelectOption } from '../atoms';
import { Tabs, DataTable, StatCard, CreateEntityModal, Modal, EntityActivityFeed, CustomFieldsPanel, ClientContactsGrouped, EntityGroupsPanel, EntityNotesPanel, DocumentUploadModal, ListToolbar, Pagination, FollowButton, LastUpdated, InlineEditCard, PropertySetupQueue, ClientInformationRequests, ForceContingencyPanel, GenerateEmailModal, useToast } from '../organisms';
import { useIsCompact } from '../lib/useMediaQuery';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';
import { usePropertyOptionsForClient } from '../lib/fkOptions';
import { useContributedTabs } from '../lib/entityTabs';
import {
  CalendarEvent, UserLite, ParcelOption, EventModal, EventLinkage, todayIso, userLabel, eventTitle, metaFor,
} from './CalendarShared';
import { formatDate, formatDateTime, formatEventDate, formatEventTime } from '../lib/orgTimezone';

/**
 * ClientDetail — /v2/clients/:id
 *
 * EntityHeader (prefix + title + status pill + actions + KPI row) above
 * a Tabs panel: Overview · Properties · Contacts · Documents.
 *
 * Phase 11 Block 7 PR 3 (2026-05-12).
 */

interface Client {
  id: string;
  name: string;
  short_name: string | null;
  status: 'active' | 'inactive' | 'in_setup' | 'historical';
  type: string | null;
  category: string | null;
  contract_type: string | null;
  contract_expires: string | null;
  initial_contract: string | null;
  website_url: string | null;
  client_group_label: string | null;
  property_location_label: string | null;
  parent_client_id: string | null;
  do_not_roll: boolean;
  client_manager_user_id: string | null;
  admin_associate_user_id: string | null;
  originator_user_id: string | null;
  in_collections?: boolean | number | null;
  ar_reminders_enabled?: boolean | number | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

interface Property {
  id: string;
  name: string;
  property_type: string | null;
  status: string;
  gba: number | null;
  units: number | null;
  // §2 column set
  state: string | null;
  address_line1: string | null;
  city: string | null;
  postal_code: string | null;
  agent_user_id: string | null;
  assessor_name: string | null;
  archived_at: string | null; // §9
}

// §1 — a parcel row for the client Parcels tab (BFF-joined to its property +
// assessor). Carries the §2 column set plus the parcel Type (RE/PP).
interface Parcel {
  id: string;
  account_number: string | null;
  re_pp: string | null;
  status: string | null;
  property_id: string | null;
  property_name: string | null;
  state: string | null;
  agent_user_id: string | null;
  property_address: string | null;
  property_city: string | null;
  property_postal_code: string | null;
  assessor_name: string | null;
  archived_at: string | null; // §9
}

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  email: string | null;
  phone_number: string | null;
}

// The single BFF payload GET /api/views/client/:id returns — the page's whole
// initial (on-mount) read collapsed into one response. Each field carries the
// SAME row shape the separate on-mount queries returned.
interface ClientView {
  client: Client;
  properties: Property[];
  parcels: Parcel[];
  contacts: Contact[];
  counts: { properties: number; parcels: number; contacts: number };
}

const statusVariant: Record<Client['status'], 'success' | 'warning' | 'neutral' | 'info'> = {
  active: 'success', inactive: 'neutral', in_setup: 'warning', historical: 'info',
};
const statusLabel: Record<Client['status'], string> = {
  active: 'Active', inactive: 'Inactive', in_setup: 'In Setup', historical: 'Historical',
};

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Edition-contributed tabs (e.g. AppealStack Law's "Matters"). Empty for a core install,
  // so the tab list, valid set, and panel switch below are unchanged when no edition is on.
  const contributed = useContributedTabs('clients', { clientId: id ?? '' });
  const [tab, setTab] = useTabState(['overview', 'properties', 'parcels', 'contacts', 'groups', 'notes', 'billing', 'calendar', 'portal', 'activity', 'custom', 'documents', ...contributed.values], 'overview');
  const groupCount = useGroupCount('client', id);
  // D2 — when the Client Portal section's "shared documents" list jumps to the
  // Documents tab, land it pre-filtered to portal-shared (one list, linked).
  const [docsInitialShared, setDocsInitialShared] = useState(false);
  const openSharedDocuments = () => { setDocsInitialShared(true); setTab('documents'); };
  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // The email templates gate on the 'notification' entity like the savings email.
  const canGenerateEmail = useEntityPermission('notification', 'can_add');
  // Invoice actions render only for holders of invoice can_add (SoD). The
  // billables list itself is gated server-side on invoice_item GET.
  // "Invoice all for client" POSTs to /api/workflow/invoice-all-for-client which
  // authorizes on the invoice PROCESS capability (not can_add) — gate on the same
  // bit so the button shows iff the server would accept it.
  const canInvoice = useEntityPermission('invoice', 'can_process');
  const canEditClient = useEntityPermission('client', 'can_edit');

  // BFF: one round-trip for the page's whole initial payload (the client record,
  // its properties, its contacts, and the header/tab counts) instead of the three
  // sequential on-mount reads this page used to fire. The per-tab lazy queries
  // (Billing/Calendar/Portal/Documents/Activity/Custom/Notes) STAY lazy below.
  const viewQuery = useQuery<ClientView>({
    queryKey: ['client-view', id],
    queryFn: ({ signal }) => api.get<ClientView>(`/api/views/client/${id}`, signal),
    enabled: !!id,
  });

  // Staff directory to resolve the client-manager / admin-associate user ids →
  // names for the header (§4). JOINs contacts server-side, so first/last present.
  const hideParentClients = useFlag(FEATURE.HIDE_PARENT_CLIENTS);
  // The firm's people, once, shared with every other user-reference render (header,
  // Team section, parcel assignments) so the header and the edit form cannot disagree.
  const users = useUserNames();

  // Read the breakpoint unconditionally (a hook) BEFORE the early returns below,
  // so the hook order stays stable across the loading → loaded transition.
  const compact = useIsCompact();

  if (viewQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading client…</p>;
  }

  if (viewQuery.error) {
    const notFound = viewQuery.error instanceof ApiClientError && viewQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Client not found' : 'Failed to load client'}</h2>
        <p style={{ margin: 0 }}>{(viewQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/clients')}>
            Back to Clients
          </Button>
        </p>
      </div>
    );
  }

  if (!viewQuery.data) return null;

  const client = viewQuery.data.client;
  const roleName = users.nameOf;
  const staffOptions = users.options;
  const properties = viewQuery.data.properties ?? [];
  const parcels = viewQuery.data.parcels ?? [];
  const contacts = viewQuery.data.contacts ?? [];
  // Header + tab counts come straight off the view payload's precomputed counts
  // (identical to the array lengths — the BFF derives them from the same rows).
  const propertyCount = viewQuery.data.counts?.properties ?? properties.length;
  const parcelCount = viewQuery.data.counts?.parcels ?? parcels.length;
  const contactCount = viewQuery.data.counts?.contacts ?? contacts.length;
  // §2 — the admin associate is a client-level role, shown as a column on both
  // the Properties and Parcels tabs (constant per client).
  const adminAssociateName = roleName(client.admin_associate_user_id);

  return (
    <>
      <EntityHeader
        prefix={client.short_name && client.short_name !== client.name ? client.short_name : undefined}
        title={client.name}
        subtitle={<>{[client.type, client.category].filter(Boolean).join(' · ') || null}<LastUpdated updatedBy={client.updated_by} updatedAt={client.updated_at} /></>}
        statusPills={<>
          <Badge variant={statusVariant[client.status]} dot>{statusLabel[client.status]}</Badge>
          {/* ATA part 4 §6: Collections is a client status Tiana sets by hand; AR reminders stop. */}
          {(client.in_collections === true || client.in_collections === 1) && <Badge variant="danger">In Collections</Badge>}
        </>}
        actions={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: compact ? 'stretch' : 'flex-end', gap: 'var(--sp-2)', width: compact ? '100%' : undefined }}>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              {canEditClient && (
                <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                  Edit all fields
                </Button>
              )}
              {canGenerateEmail && (
                <Button variant="secondary" size="sm" onClick={() => setEmailOpen(true)}>
                  Generate email
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={() => navigate(`/properties/new?client_id=${client.id}`)}>
                New property
              </Button>
              <FollowButton entityType="client" entityId={client.id} />
            </div>
            {/* §4 — client manager + admin associate, beneath the Follow button.
                Agent is a PROPERTY-level role (rolls up), shown on the Properties/
                Parcels tabs, not here. */}
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', textAlign: compact ? 'left' : 'right', lineHeight: 'var(--lh-normal, 1.5)' }}>
              {/* Assignment model (Yadi 2026-09-16): the client team is exactly Manager and
                  Assistant, read from the same two columns the Team section edits. */}
              <div>Manager: <strong style={{ color: 'var(--color-ink-2)' }}>{roleName(client.client_manager_user_id)}</strong></div>
              <div>Assistant: <strong style={{ color: 'var(--color-ink-2)' }}>{roleName(client.admin_associate_user_id)}</strong></div>
              <div>Originator: <strong style={{ color: 'var(--color-ink-2)' }}>{roleName(client.originator_user_id)}</strong></div>
            </div>
          </div>
        }
        kpis={
          <>
            <StatCard label="Properties" value={String(propertyCount)} caption="across this client" accent="hunter" />
            <StatCard label="Contacts" value={String(contactCount)} caption="people on file" accent="info" />
            <StatCard
              label="Contract"
              value={client.contract_type || '—'}
              caption={client.contract_expires ? `Expires ${formatDate(client.contract_expires)}` : 'No expiry on file'}
              accent="amber"
            />
            <StatCard
              label="Annual roll"
              value={client.do_not_roll ? 'Frozen' : 'Active'}
              caption={client.do_not_roll ? 'Excluded from year roll' : 'Rolls each tax year'}
              accent={client.do_not_roll ? 'amber' : 'green'}
            />
          </>
        }
      />

      <OnboardingGateBanner clientId={client.id} onReview={() => setTab('documents')} />

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'overview', label: 'Overview' },
          { value: 'properties', label: 'Properties', count: propertyCount },
          { value: 'parcels', label: 'Parcels', count: parcelCount },
          { value: 'contacts', label: 'Contacts', count: contactCount },
          { value: 'groups', label: groupTabLabel(groupCount) },
          { value: 'notes', label: 'Notes' },
          { value: 'billing', label: 'Billing' },
          { value: 'calendar', label: 'Calendar' },
          { value: 'portal', label: 'Client Portal' },
          { value: 'activity', label: 'Activity' },
          { value: 'custom', label: 'Custom Fields' },
          { value: 'documents', label: 'Documents & Contracts' },
          ...contributed.tabItems,
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'overview' && <OverviewTab client={client} staffOptions={staffOptions} nameOf={roleName} />}
        {tab === 'properties' && <PropertiesTab properties={properties} loading={viewQuery.isLoading} resolveAgent={roleName} adminAssociate={adminAssociateName} onRowClick={(p) => navigate(`/properties/${p.id}`)} />}
        {tab === 'parcels' && <ParcelsTab parcels={parcels} loading={viewQuery.isLoading} resolveAgent={roleName} adminAssociate={adminAssociateName} onRowClick={(p) => navigate(`/parcels/${p.id}`)} onPropertyClick={(propertyId) => navigate(`/properties/${propertyId}`)} />}
        {tab === 'contacts' && <ClientContactsGrouped clientId={client.id} />}
        {tab === 'groups' && <EntityGroupsPanel entityType="client" entityId={client.id} />}
        {tab === 'notes' && <EntityNotesPanel entityType="clients" entityId={client.id} />}
        {tab === 'billing' && (
          <>
            <ArSettingsPanel client={client} canEdit={canEditClient} />
            <BillingTab clientId={client.id} canInvoice={canInvoice} />
          </>
        )}
        {tab === 'calendar' && <ClientCalendarTab clientId={client.id} clientName={client.name} properties={properties} />}
        {tab === 'portal' && <><PropertySetupQueue clientId={client.id} /><ClientInformationRequests clientId={client.id} /><PortalAccessTab clientId={client.id} clientName={client.name} properties={properties} onOpenSharedDocuments={openSharedDocuments} /></>}
        {tab === 'documents' && <DocumentsTab clientId={client.id} properties={properties} initialShared={docsInitialShared} onSharedConsumed={() => setDocsInitialShared(false)} />}
        {tab === 'activity' && <EntityActivityFeed entityType="clients" entityId={client.id} />}
        {tab === 'custom' && <CustomFieldsPanel entityType="clients" entityId={client.id} />}
        {contributed.panels[tab]}
      </div>

      <GenerateEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        scope={{ entityType: 'clients', entityId: client.id, label: client.name }}
      />

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="clients"
        recordId={client.id}
        // §0 role model — the client-manager + admin-associate FK fields render as
        // staff pickers (not raw id inputs) in the edit form.
        fkOptions={{ client_manager_user_id: staffOptions, admin_associate_user_id: staffOptions, originator_user_id: staffOptions }}
        // People pickers filter by typing at any firm size; the parent-client field goes
        // when the org hides the Parent Clients concept (feat:hide_parent_clients).
        searchableFields={['client_manager_user_id', 'admin_associate_user_id', 'originator_user_id']}
        hideFields={hideParentClients ? ['parent_client_id'] : []}
        // ['client-view', id] refreshes THIS page's collapsed BFF payload after an
        // edit; ['client', id] keeps the breadcrumb (useBreadcrumbs) fresh too.
        invalidateKeys={[['clients-list'], ['client', client.id], ['client-view', client.id]]}
      />
    </>
  );
}

// latestAttr / humanizeToken / registryOptions / AttrMap now live in
// lib/inlineAttribution (shared by every Part A profile overview).

type TeamOption = { value: string; label: string };

function OverviewTab({ client, staffOptions, nameOf }: {
  client: Client;
  /** Active internal users, sorted by name (useUserNames().options). */
  staffOptions: TeamOption[];
  nameOf: (id: unknown) => string;
}) {
  const hideParentClients = useFlag(FEATURE.HIDE_PARENT_CLIENTS);
  // Parent-client (umbrella) linkage — when this client rolls up under a parent
  // (clients.parent_client_id), surface it here and link to the parent profile.
  const parentQuery = useQuery<{ id: string; name: string | null }>({
    queryKey: ['parent-client', client.parent_client_id],
    queryFn: ({ signal }) => api.get<{ id: string; name: string | null }>(`/api/parent-clients/${client.parent_client_id}`, signal),
    enabled: !!client.parent_client_id,
  });

  // §1.2 — per-FIELD attribution (who last changed each field + when). Each box
  // below derives its OWN "last updated by · when" from its fields, so editing
  // Identity never restamps Contract/References.
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'clients', client.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/clients/${client.id}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const record = client as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['client-view', client.id], ['client', client.id], ['field-attribution', 'clients', client.id]];
  // Inline-edit cards default to editable; gate on client.can_edit so a view-only role
  // (training) / agent can't edit. Hook — before the return.
  const canEditClient = useEntityPermission('client', 'can_edit');

  // §1b — Type + Category are org-configurable dropdowns (Org Settings registries).
  const clientTypes = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'client-types'], queryFn: ({ signal }) => api.get('/api/org-settings/client-types', signal), staleTime: 5 * 60 * 1000 });
  const clientCategories = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'client-categories'], queryFn: ({ signal }) => api.get('/api/org-settings/client-categories', signal), staleTime: 5 * 60 * 1000 });
  // Contract type is an org-configurable dropdown too (reviewer 7/25 — the Contract
  // box should match the Identity Type/Category treatment).
  const contractTypes = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'contract-types'], queryFn: ({ signal }) => api.get('/api/org-settings/contract-types', signal), staleTime: 5 * 60 * 1000 });

  // The client team, edited in place. A current assignee who has since been deactivated is
  // not in the picker list, so add them back under their name; the card would otherwise
  // offer the raw user id as the selected option.
  const teamOptions = (column: keyof Client): TeamOption[] => {
    const current = client[column];
    if (!current || staffOptions.some((o) => o.value === current)) return staffOptions;
    return [{ value: String(current), label: nameOf(current) }, ...staffOptions];
  };

  return (
    <>
      {client.parent_client_id && !hideParentClients && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          padding: 'var(--sp-2) var(--sp-3)', marginBottom: 'var(--sp-4)',
          background: 'var(--brand-primary-bg)', border: '1px solid var(--brand-primary)',
          borderRadius: 'var(--r-lg)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
        }}>
          <span>
            Part of{' '}
            <Link to={`/parent-clients/${client.parent_client_id}`} style={{ color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}>
              {parentQuery.data?.name ?? 'parent client'}
            </Link>
          </span>
        </div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        <InlineEditCard
          title="Identity"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['name', 'short_name', 'type', 'category'])}
          fields={[
            { key: 'name', label: 'Full name', required: true },
            { key: 'short_name', label: 'Short name', required: true },
            { key: 'type', label: 'Type', type: 'select', options: registryOptions(clientTypes.data), addOptionSlug: 'client-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
            { key: 'category', label: 'Category', type: 'select', options: registryOptions(clientCategories.data), addOptionSlug: 'client-categories', display: (v) => (v ? humanizeToken(String(v)) : '—') },
          ]}
        />
        <InlineEditCard
          title="Contract"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['contract_type', 'initial_contract', 'contract_expires', 'do_not_roll'])}
          fields={[
            { key: 'contract_type', label: 'Contract type', type: 'select', options: registryOptions(contractTypes.data), addOptionSlug: 'contract-types', display: (v) => (v ? humanizeToken(String(v)) : '—') },
            { key: 'initial_contract', label: 'Initial', type: 'date', display: (v) => formatDate(v as string | null) },
            { key: 'contract_expires', label: 'Expires', type: 'date', display: (v) => formatDate(v as string | null) },
            { key: 'do_not_roll', label: 'Do not roll', type: 'checkbox' },
          ]}
        />
        <InlineEditCard
          title="References"
          routePath="clients"
          recordId={client.id}
          record={record}
          canEdit={canEditClient}
          invalidateKeys={invalidate}
          attribution={latestAttr(attr, ['client_group_label', 'property_location_label', 'website_url'])}
          fields={[
            { key: 'client_group_label', label: 'Group label' },
            { key: 'property_location_label', label: 'Location label' },
            {
              key: 'website_url', label: 'Website', type: 'url',
              display: (v) => (v ? <a href={String(v)} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-primary)' }}>{String(v)}</a> : '—'),
            },
          ]}
        />
        {/* The header already names the team for every reader; this card is where a holder
            of client can_edit changes it, so it is not rendered for anyone else. */}
        {canEditClient && (
          <InlineEditCard
            title="Team"
            routePath="clients"
            recordId={client.id}
            record={record}
            canEdit={canEditClient}
            invalidateKeys={invalidate}
            attribution={latestAttr(attr, ['client_manager_user_id', 'admin_associate_user_id', 'originator_user_id'])}
            fields={[
              { key: 'client_manager_user_id', label: 'Manager', type: 'searchable-select', options: teamOptions('client_manager_user_id'), display: nameOf },
              { key: 'admin_associate_user_id', label: 'Assistant', type: 'searchable-select', options: teamOptions('admin_associate_user_id'), display: nameOf },
              { key: 'originator_user_id', label: 'Originator', type: 'searchable-select', options: teamOptions('originator_user_id'), display: nameOf },
            ]}
          />
        )}
      </div>
    </>
  );
}

// §1/§2 shared display helpers. RE/PP is stored as an enum; render the short
// tag operators use. Address collapses line1 · city · zip into one cell.
function rePpLabel(v: string | null | undefined): string {
  if (v === 'real_estate') return 'RE';
  if (v === 'personal_property') return 'PP';
  if (v === 'real_and_personal') return 'RE/PP';
  return '—';
}
function addressCell(line1: string | null, city: string | null, zip: string | null): string {
  const parts = [line1, [city, zip].filter(Boolean).join(' ')].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}
// A clickable link cell that navigates without also firing the row's onRowClick.
function LinkCell({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: 'var(--brand-primary)', font: 'inherit', textAlign: 'left', textDecoration: 'underline',
      }}
    >{label}</button>
  );
}

// §2 — the client Properties tab column set: State · Property name · Assessor ·
// Property address/city/zip · Agent (property-level, rolled up) · Assistant (client-level).
function PropertiesTab({
  properties, loading, resolveAgent, adminAssociate, onRowClick,
}: {
  properties: Property[];
  loading: boolean;
  resolveAgent: (uid: string | null) => string;
  adminAssociate: string;
  onRowClick: (p: Property) => void;
}) {
  const columns = useMemo<ColumnDef<Property>[]>(() => [
    { accessorKey: 'state', header: 'State', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'name', header: 'Property' },
    { accessorKey: 'assessor_name', header: 'Assessor', cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'address', header: 'Address', cell: ({ row }) => addressCell(row.original.address_line1, row.original.city, row.original.postal_code) },
    { id: 'agent', header: 'Agent', cell: ({ row }) => resolveAgent(row.original.agent_user_id) },
    { id: 'admin_associate', header: 'Assistant', cell: () => adminAssociate },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{humanizeToken((getValue() as string) || '')}</Badge> },
  ], [resolveAgent, adminAssociate]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading properties…</p>;
  if (properties.length === 0) {
    return <Empty message="No properties on this client yet." />;
  }
  // §9 — archived properties MOVE to their own Archive section (off the active list).
  const active = properties.filter((p) => !p.archived_at);
  const archived = properties.filter((p) => p.archived_at);
  return (
    <>
      {active.length === 0
        ? <Empty message="No active properties on this client." />
        : <DataTable data={active} columns={columns} onRowClick={onRowClick} />}
      {archived.length > 0 && (
        <ArchiveSection label="Archived properties" count={archived.length}>
          <DataTable data={archived} columns={columns} onRowClick={onRowClick} />
        </ArchiveSection>
      )}
    </>
  );
}

// §9 — a collapsible section that holds the archived rows beneath the active list.
function ArchiveSection({ label, count, children }: { label: string; count: number; children: React.ReactNode }) {
  return (
    <details style={{ marginTop: 'var(--sp-5)' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', padding: 'var(--sp-2) 0' }}>
        <IconArchive size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> {label} ({count})
      </summary>
      <div style={{ marginTop: 'var(--sp-3)' }}>{children}</div>
    </details>
  );
}

// §1 — the client Parcels tab: every parcel across the client's properties, with
// a clickable Property column (→ property profile) and a Type (RE/PP) column, on
// the §2 column set. Account # is the row identifier (row → parcel profile).
function ParcelsTab({
  parcels, loading, resolveAgent, adminAssociate, onRowClick, onPropertyClick,
}: {
  parcels: Parcel[];
  loading: boolean;
  resolveAgent: (uid: string | null) => string;
  adminAssociate: string;
  onRowClick: (p: Parcel) => void;
  onPropertyClick: (propertyId: string) => void;
}) {
  const columns = useMemo<ColumnDef<Parcel>[]>(() => [
    { accessorKey: 'account_number', header: 'Parcel', cell: ({ getValue }) => (
      <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{(getValue() as string) || '—'}</span>
    ) },
    { id: 'property', header: 'Property', cell: ({ row }) => (
      row.original.property_id
        ? <LinkCell label={row.original.property_name || '—'} onClick={() => onPropertyClick(row.original.property_id!)} />
        : (row.original.property_name || '—')
    ) },
    { accessorKey: 're_pp', header: 'Type', cell: ({ getValue }) => <Badge variant="neutral">{rePpLabel(getValue() as string)}</Badge> },
    { accessorKey: 'state', header: 'State', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'assessor_name', header: 'Assessor', cell: ({ getValue }) => (getValue() as string) || '—' },
    { id: 'address', header: 'Address', cell: ({ row }) => addressCell(row.original.property_address, row.original.property_city, row.original.property_postal_code) },
    { id: 'agent', header: 'Agent', cell: ({ row }) => resolveAgent(row.original.agent_user_id) },
    { id: 'admin_associate', header: 'Assistant', cell: () => adminAssociate },
  ], [resolveAgent, adminAssociate, onPropertyClick]);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading parcels…</p>;
  if (parcels.length === 0) {
    return <Empty message="No parcels on this client yet." />;
  }
  // §9 — archived parcels MOVE to their own Archive section (off the active list).
  const active = parcels.filter((p) => !p.archived_at);
  const archived = parcels.filter((p) => p.archived_at);
  return (
    <>
      {active.length === 0
        ? <Empty message="No active parcels on this client." />
        : <DataTable data={active} columns={columns} onRowClick={onRowClick} />}
      {archived.length > 0 && (
        <ArchiveSection label="Archived parcels" count={archived.length}>
          <DataTable data={archived} columns={columns} onRowClick={onRowClick} />
        </ArchiveSection>
      )}
    </>
  );
}

interface Billable {
  id: string;
  parcel_id: string | null;
  parcel_account: string | null;
  parcel_name: string | null;
  billing_type: string | null;
  reduction: number | null;
  amount: number | null;
  description: string | null;
}

interface InvoiceAllResult {
  success?: boolean;
  invoiceId?: string;
  invoiceNumber?: string;
  amount?: number;
  // The PRIMARY grouping, when the result was split at all: owner/LLC (04.2), or
  // real estate vs personal property when only that split applied.
  mode?: 'economic_unit' | 're_pp';
  // Present whenever the result is more than one invoice — for ANY reason. Read
  // this rather than `mode` to decide how to report: the two splits compose, so
  // a new split arriving later must not silently fall through to the
  // single-invoice branch and report an undefined invoice number.
  invoices?: Array<{
    economic_unit_id: string | null;
    economic_unit_name: string | null;
    re_pp: string | null;
    invoiceId?: string;
    invoiceNumber?: string;
    amount?: number;
  }>;
}

// Owner-invoice layout (04.8): the 7 columns per parcel, grouped per owner/LLC.
interface OwnerInvoiceLine {
  invoice_item_id: string;
  parcel_id: string | null;
  parcel_account: string | null;
  parcel_name: string | null;
  fee_structure: string | null;
  proposed_value: number | null;
  final_value: number | null;
  reduction: number | null;
  rate: number | null;
  fee: number | null;
  tax_savings: number | null;
  total_due: number | null;
}
interface OwnerInvoiceGroup {
  economic_unit_id: string | null;
  economic_unit_name: string | null;
  parcels: OwnerInvoiceLine[];
  subtotal_fee: number | null;
  subtotal_tax_savings: number | null;
  subtotal_total_due: number | null;
}
interface OwnerInvoiceSummaryData {
  client_id: string;
  redacted: boolean;
  groups: OwnerInvoiceGroup[];
}

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

// A stored fee/tax rate is a fraction (e.g. 0.30). Render as a percentage.
const pct = (v: number | null | undefined) =>
  v != null ? `${(v * 100).toFixed(v * 100 % 1 === 0 ? 0 : 2)}%` : '—';

const billingTypeLabel: Record<string, string> = {
  contingency: 'Contingency',
  flat_fee: 'Flat fee',
  hybrid: 'Hybrid',
};

/**
 * BillingTab — the biller's daily loop for one client: what's staged to bill,
 * and one-click invoice-all. Billable items stage automatically when appeal
 * outcomes are recorded; the calculate-and-stage trigger lands with its BE
 * endpoint (contract point B), so no dead control is shown here yet.
 */
interface ClientInvoice {
  id: string;
  invoice_number: string | null;
  status: string | null;
  amount: number | null;
  balance: number | null;
  year: number | null;
}

const invoiceStatusVariant: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  paid: 'success', partially_paid: 'info', sent: 'info', approved: 'info',
  prepared: 'neutral', pending: 'warning', void: 'danger', cancelled: 'danger',
};

const clientInvoiceColumns: ColumnDef<ClientInvoice>[] = [
  { accessorKey: 'invoice_number', header: 'Invoice #', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>{String(getValue() ?? '—')}</span> },
  { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => String(getValue() ?? '—') },
  { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => { const s = String(getValue() ?? '—'); return <Badge variant={invoiceStatusVariant[s] ?? 'neutral'}>{s.replace(/_/g, ' ')}</Badge>; } },
  { accessorKey: 'amount', header: 'Amount', cell: ({ getValue }) => money(getValue() as number | null) },
  { accessorKey: 'balance', header: 'Balance', cell: ({ getValue }) => money(getValue() as number | null) },
];

/**
 * ArSettingsPanel — the two accounts-receivable switches on a client (ATA part 4 §6): In Collections
 * (shown as a badge on the header and on the AR views; automated reminders stop) and AR reminders
 * (off by default; when on, a reminder drafts once an invoice passes 30 days past due, then every
 * 30 days while anything stays open). Both write the client row through the generic PUT.
 */
function ArSettingsPanel({ client, canEdit }: { client: Client; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const truthy = (v: unknown) => v === true || v === 1;
  const mutation = useMutation({
    mutationFn: (patch: { in_collections?: boolean; ar_reminders_enabled?: boolean }) => api.put(`/api/clients/${client.id}`, patch),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['client', client.id] }); queryClient.invalidateQueries({ queryKey: ['ar-summary'] }); },
    onError: (e) => toast({ variant: 'error', message: e instanceof ApiClientError && e.body && typeof e.body === 'object' && 'error' in e.body ? String((e.body as { error: unknown }).error) : (e as Error).message }),
  });
  return (
    <div data-testid="ar-settings" style={{ display: 'flex', gap: 'var(--sp-5)', alignItems: 'center', flexWrap: 'wrap', padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Accounts receivable</span>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
        <input type="checkbox" aria-label="In collections" checked={truthy(client.in_collections)} disabled={!canEdit || mutation.isPending} onChange={(e) => mutation.mutate({ in_collections: e.target.checked })} />
        In collections
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
        <input type="checkbox" aria-label="Automated AR reminders" checked={truthy(client.ar_reminders_enabled)} disabled={!canEdit || mutation.isPending} onChange={(e) => mutation.mutate({ ar_reminders_enabled: e.target.checked })} />
        Automated AR reminders
      </label>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Reminders draft the AR Aging Reminder once an invoice passes 30 days past due, then every 30 days, and stop in Collections.</span>
    </div>
  );
}

function BillingTab({ clientId, canInvoice }: { clientId: string; canInvoice: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Grouping mode (04.2): 'client' = one invoice for the whole client (default),
  // 'economic_unit' = one invoice per owner/LLC (parcels grouped by economic unit).
  const [mode, setMode] = useState<'client' | 'economic_unit'>('client');
  // §1.6 — raise an invoice from here. "Invoice all for client" only appears when
  // something is staged, so a client with nothing billable had no way to start an
  // invoice at all; free-form charges (the common case) are added as line items
  // afterwards, which is why this must not depend on billables existing.
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const clientProperties = usePropertyOptionsForClient(clientId, newInvoiceOpen);

  const billablesQuery = useQuery<Billable[]>({
    queryKey: ['billables', clientId],
    queryFn: ({ signal }) => api.get<Billable[]>(`/api/invoice-items-billable/${clientId}`, signal),
  });

  // The client's actual invoices (not just pre-bill billables) so the Billing tab
  // links straight into each invoice — the client-profile ↔ invoices bridge.
  // Server-filtered by client_id (crud SCALAR_FILTERS); SoD row-scope still applies.
  const invoicesQuery = useQuery<ClientInvoice[]>({
    queryKey: ['client-invoices', clientId],
    queryFn: ({ signal }) => api.get<ClientInvoice[]>(`/api/invoices?client_id=${clientId}`, signal),
  });

  // Owner-invoice layout (04.8): the 7 columns per parcel, grouped per owner/LLC.
  const summaryQuery = useQuery<OwnerInvoiceSummaryData>({
    queryKey: ['owner-invoice-summary', clientId],
    queryFn: ({ signal }) => api.get<OwnerInvoiceSummaryData>(`/api/owner-invoice-summary/${clientId}`, signal),
  });

  const invoiceAll = useMutation({
    mutationFn: (groupBy: 'client' | 'economic_unit') =>
      api.post<InvoiceAllResult>(
        `/api/workflow/invoice-all-for-client/${clientId}`,
        groupBy === 'economic_unit' ? { group_by: 'economic_unit' } : {},
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['billables', clientId] });
      queryClient.invalidateQueries({ queryKey: ['owner-invoice-summary', clientId] });
      // The Billing tab lists the client's actual invoices — refresh it so the
      // just-created invoice(s) appear when the operator returns to this tab.
      queryClient.invalidateQueries({ queryKey: ['client-invoices', clientId] });
      // Any split result — keyed on `invoices` being there, not on which split
      // produced it, so a result carrying a grouping this build has not heard of
      // still reports the right count instead of an undefined invoice number.
      if (res.invoices) {
        const n = res.invoices.length;
        const noun = res.mode === 'economic_unit' ? 'owner invoice' : 'invoice';
        toast({ variant: 'success', message: `${n} ${noun}${n === 1 ? '' : 's'} created.` });
        const first = res.invoices.find((i) => i.invoiceId);
        if (first?.invoiceId) navigate(`/invoices/${first.invoiceId}`);
        return;
      }
      toast({ variant: 'success', message: res.invoiceNumber ? `Invoice ${res.invoiceNumber} created.` : 'Invoice created.' });
      if (res.invoiceId) navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  if (billablesQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading billables…</p>;
  if (billablesQuery.error) {
    // A permission-denied (a role without invoice_item view — e.g. some agents/analysts)
    // should read as a clean "restricted" state, not a scary red error box.
    const msg = (billablesQuery.error as Error).message;
    const restricted = /permission denied/i.test(msg);
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: restricted ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: `1px solid ${restricted ? 'var(--color-border)' : 'var(--color-status-red)'}`,
        color: restricted ? 'var(--color-ink-3)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{restricted ? 'Billing details are restricted for your role.' : msg}</div>
    );
  }

  const billables = billablesQuery.data ?? [];
  const totalFee = billables.reduce((s, b) => s + (b.amount || 0), 0);
  const groups = summaryQuery.data?.groups ?? [];
  const ownerCount = groups.filter((g) => g.economic_unit_id).length;
  // Money-blind role: the server nulls invoice_items.amount, so summing yields 0 and
  // money(0) would render a misleading "$0 ready to invoice". Surface the redacted
  // state instead (the owner-summary view below already does this via `redacted`).
  const redacted = (summaryQuery.data?.redacted ?? false)
    || (billables.length > 0 && billables.every((b) => b.amount == null));

  const modeOptions: SelectOption[] = [
    { value: 'client', label: 'One invoice for client' },
    { value: 'economic_unit', label: 'One invoice per owner / LLC' },
  ];

  return (
    <>
      <div style={{ marginBottom: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 auto', minWidth: 220 }}>
          <StatCard
            label="Ready to invoice"
            value={redacted ? '—' : money(totalFee)}
            caption={redacted
              ? `${billables.length} billable ${billables.length === 1 ? 'item' : 'items'} · dollar figures hidden for your role`
              : `${billables.length} billable ${billables.length === 1 ? 'item' : 'items'}${ownerCount > 0 ? ` · ${ownerCount} owner${ownerCount === 1 ? '' : 's'}` : ''}`}
            accent="green"
          />
        </div>
        {canInvoice && (
          <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {billables.length > 0 && (
              <>
                <div style={{ minWidth: 220 }}>
                  <Select
                    options={modeOptions}
                    value={mode}
                    onChange={(e) => setMode(e.target.value as 'client' | 'economic_unit')}
                    size="sm"
                    aria-label="Invoice grouping mode"
                  />
                </div>
                <Button
                  variant="primary"
                  size="md"
                  loading={invoiceAll.isPending}
                  onClick={() => invoiceAll.mutate(mode)}
                >
                  {mode === 'economic_unit' ? 'Invoice per owner' : 'Invoice all for client'}
                </Button>
              </>
            )}
            <Button variant="secondary" size="md" onClick={() => setNewInvoiceOpen(true)}>
              New invoice
            </Button>
          </div>
        )}
      </div>

      {canInvoice && (
        <NewInvoiceModal
          open={newInvoiceOpen}
          onClose={() => setNewInvoiceOpen(false)}
          clientId={clientId}
          propertyOptions={clientProperties.options}
        />
      )}

      {billables.length === 0 ? (
        <Empty message="Nothing staged to invoice. Billable items appear here as appeal outcomes are recorded." />
      ) : (
        <OwnerInvoiceSummaryView
          query={summaryQuery}
          redacted={summaryQuery.data?.redacted ?? false}
        />
      )}

      {/* This client's actual invoices — click through to edit an invoice + its
          line items. Closes the Billing-tab ↔ Invoices ↔ Invoice-items loop. */}
      <div style={{ marginTop: 'var(--sp-7)' }}>
        <h3 style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Invoices</h3>
        {invoicesQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)' }}>Loading invoices…</p>
        ) : (invoicesQuery.data ?? []).length === 0 ? (
          <Empty message="No invoices for this client yet." />
        ) : (
          <DataTable
            data={invoicesQuery.data ?? []}
            columns={clientInvoiceColumns}
            onRowClick={(inv) => navigate(`/invoices/${inv.id}`)}
          />
        )}
      </div>

      {/* A14 — the items that have NOT met their billing trigger, and the gated
          override that bills them early. Renders nothing at all unless the viewer
          is a super admin / office administrator AND there is something forceable,
          so it is invisible on the ordinary biller's screen. */}
      <ForceContingencyPanel clientId={clientId} />
    </>
  );
}

/**
 * NewInvoiceModal (§1.6) — start an invoice for this client with nothing on it.
 *
 * Deliberately NOT the schema-driven CreateEntityModal. `invoices.invoice_number`
 * is NOT NULL and comes from a per-org monotonic counter, so a generic form either
 * blocks on a required field the operator cannot know or invites them to type a
 * number that collides with the next generated one. `POST /api/billing/invoices`
 * mints it from the same counter `generateInvoice` uses.
 *
 * The client is fixed by the tab this opened from — the only choice here is the
 * optional property, and the options are that client's own, so the picker cannot
 * offer a building belonging to somebody else.
 */
function NewInvoiceModal({
  open, onClose, clientId, propertyOptions,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  propertyOptions: SelectOption[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [propertyId, setPropertyId] = useState<string | null>(null);
  // ATA part 4 §7: the invoice type as ATA bills it. Performance is the everyday case.
  const [type, setType] = useState('performance');

  // Clear the picker between openings — a stale property from a previous attempt
  // would silently attach itself to the next invoice.
  useEffect(() => { if (open) { setPropertyId(null); setType('performance'); } }, [open]);

  const create = useMutation({
    mutationFn: () => api.post<{ id: string; invoiceNumber: string }>(
      '/api/billing/invoices',
      { client_id: clientId, type, ...(propertyId ? { property_id: propertyId } : {}) },
    ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices', clientId] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      toast({ variant: 'success', message: `Invoice ${res.invoiceNumber} created.` });
      onClose();
      navigate(`/invoices/${res.id}`);
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New invoice"
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" loading={create.isPending} onClick={() => create.mutate()}>
            Create invoice
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        Opens an empty invoice for this client. Add charges to it as line items —
        they do not have to come from staged billable items.
      </p>
      <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
        <span style={{
          display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)',
        }}>Type</span>
        <Select
          aria-label="Invoice type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={[
            { value: 'performance', label: 'Performance' },
            { value: 'litigation', label: 'Litigation' },
            { value: 'base_fee', label: 'Base Fee' },
            { value: 'flat_fee', label: 'Flat Fee' },
            { value: 'tax_estimate', label: 'Tax Estimate' },
          ]}
        />
        <span style={{ display: 'block', marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Base Fee, Flat Fee and Tax Estimate are fixed amounts, never split, and stay out of the commission report.
        </span>
      </label>
      <label style={{ display: 'block' }}>
        <span style={{
          display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)',
        }}>Property (optional)</span>
        <SearchableSelect
          options={propertyOptions}
          value={propertyId}
          onChange={setPropertyId}
          placeholder="Search this client's properties…"
          emptyLabel="No property"
          aria-label="Property"
        />
      </label>
    </Modal>
  );
}

/**
 * OwnerInvoiceSummaryView (04.8) — renders the owner-invoice layout from
 * GET /api/owner-invoice-summary/:clientId: one card per owner/LLC (economic
 * unit), each a table of its parcels with the 7 owner-invoice columns and an
 * owner subtotal. A money-blind role receives the figures nulled server-side;
 * the layout still renders (dashes) so the structure is visible.
 */
function OwnerInvoiceSummaryView({
  query, redacted,
}: {
  query: ReturnType<typeof useQuery<OwnerInvoiceSummaryData>>;
  redacted: boolean;
}) {
  if (query.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading owner-invoice layout…</p>;
  if (query.error) return <ErrorBox error={query.error as Error} />;
  const groups = query.data?.groups ?? [];
  if (groups.length === 0) return <Empty message="No billable items to lay out." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      {redacted && (
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Dollar figures are hidden for your role. Owner and parcel structure is shown.
        </div>
      )}
      {groups.map((g) => (
        <OwnerGroupCard key={g.economic_unit_id ?? '__ungrouped__'} group={g} />
      ))}
    </div>
  );
}

function OwnerGroupCard({ group }: { group: OwnerInvoiceGroup }) {
  const th: React.CSSProperties = {
    textAlign: 'right', padding: 'var(--sp-2) var(--sp-3)', fontSize: 'var(--fs-xs)',
    fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink-3)', textTransform: 'uppercase',
    letterSpacing: 'var(--ls-wide)', whiteSpace: 'nowrap',
  };
  const thLeft: React.CSSProperties = { ...th, textAlign: 'left' };
  const td: React.CSSProperties = {
    textAlign: 'right', padding: 'var(--sp-2) var(--sp-3)', fontSize: 'var(--fs-sm)',
    color: 'var(--color-ink)', borderTop: '1px solid var(--color-border)', whiteSpace: 'nowrap',
  };
  const tdLeft: React.CSSProperties = { ...td, textAlign: 'left' };

  return (
    <section style={{
      background: 'var(--color-card)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)', overflow: 'hidden',
    }}>
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-4)', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap',
      }}>
        <div style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          {group.economic_unit_name || (group.economic_unit_id ? 'Owner' : 'Direct (no economic unit)')}
        </div>
        <Badge variant={group.economic_unit_id ? 'info' : 'neutral'}>
          {group.parcels.length} parcel{group.parcels.length === 1 ? '' : 's'}
        </Badge>
      </header>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr>
              <th style={thLeft}>Parcel</th>
              <th style={th}>Proposed value</th>
              <th style={th}>Final value</th>
              <th style={th}>Rate</th>
              <th style={th}>Fee</th>
              <th style={thLeft}>Contingency</th>
              <th style={th}>Tax savings</th>
              <th style={th}>Total due</th>
            </tr>
          </thead>
          <tbody>
            {group.parcels.map((p) => (
              <tr key={p.invoice_item_id}>
                <td style={tdLeft}>
                  <div style={{ fontWeight: 'var(--fw-medium)' }}>{p.parcel_name || '—'}</div>
                  {p.parcel_account && (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                      {p.parcel_account}
                    </div>
                  )}
                </td>
                <td style={td}>{money(p.proposed_value)}</td>
                <td style={td}>{money(p.final_value)}</td>
                <td style={td}>{pct(p.rate)}</td>
                <td style={td}>{money(p.fee)}</td>
                <td style={tdLeft}>
                  {p.fee_structure
                    ? <Badge variant="neutral">{billingTypeLabel[p.fee_structure] || p.fee_structure.replace(/_/g, ' ')}</Badge>
                    : '—'}
                </td>
                <td style={td}>{money(p.tax_savings)}</td>
                <td style={{ ...td, fontWeight: 'var(--fw-semibold)' }}>{money(p.total_due)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ ...tdLeft, fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink-3)' }}>Owner subtotal</td>
              <td style={td} />
              <td style={td} />
              <td style={td} />
              <td style={{ ...td, fontWeight: 'var(--fw-semibold)' }}>{money(group.subtotal_fee)}</td>
              <td style={tdLeft} />
              <td style={{ ...td, fontWeight: 'var(--fw-semibold)' }}>{money(group.subtotal_tax_savings)}</td>
              <td style={{ ...td, fontWeight: 'var(--fw-semibold)' }}>{money(group.subtotal_total_due)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/**
 * ClientCalendarTab — the per-client Calendar surface staff use to continuously
 * manage this client's events. Loads GET /api/calendar/events?client_id=<id>
 * (server-side org + book scoped — the same predicate the org calendar uses), and
 * splits into Upcoming / Past. "+ New event" and every row open the SAME shared
 * EventModal, PRE-LINKED to this client (client fixed) with the parcel picker
 * limited to this client's parcels. Create / edit / delete happen inline.
 */
function ClientCalendarTab({ clientId, clientName, properties }: { clientId: string; clientName: string; properties: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const canView = useEntityPermission('event', 'can_view');
  const canAdd = useEntityPermission('event', 'can_add');
  const canEdit = useEntityPermission('event', 'can_edit');
  const canDelete = useEntityPermission('event', 'can_delete');

  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [creating, setCreating] = useState(false);

  const eventsQuery = useQuery<CalendarEvent[]>({
    queryKey: ['client-calendar-events', clientId],
    queryFn: ({ signal }) => api.get<CalendarEvent[]>(`/api/calendar/events?client_id=${clientId}`, signal),
    enabled: canView && !!clientId,
  });
  const usersQuery = useQuery<UserLite[]>({
    queryKey: ['calendar-users-list'],
    queryFn: ({ signal }) => api.get<UserLite[]>('/api/users-list', signal),
    enabled: canView,
  });
  const parcelsQuery = useQuery<ParcelOption[]>({
    queryKey: ['calendar-parcels-list'],
    queryFn: ({ signal }) => api.get<ParcelOption[]>('/api/parcels', signal),
    enabled: canView,
  });

  // Events created/edited here also appear on the main Calendar (['calendar-events'])
  // and the Dashboard "Upcoming Events" box (['calendar-upcoming']) — refresh all
  // three so those surfaces don't lag behind this tab (symmetric to CalendarPage).
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['client-calendar-events', clientId] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-upcoming'] });
  };
  const createMut = useMutation({
    mutationFn: (body: Partial<CalendarEvent>) => api.post<CalendarEvent>('/api/calendar/events', body),
    onSuccess: () => { invalidate(); setCreating(false); toast({ variant: 'success', message: 'Event created.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not create the event.' }),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CalendarEvent> }) => api.put<CalendarEvent>(`/api/calendar/events/${id}`, body),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event updated.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not update the event.' }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/calendar/events/${id}`),
    onSuccess: () => { invalidate(); setEditing(null); toast({ variant: 'success', message: 'Event deleted.' }); },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not delete the event.' }),
  });

  // The parcel picker is limited to THIS client's parcels: parcels whose owning
  // property belongs to the client (parcels carry property_id; the client's
  // property ids come from the already-loaded Properties tab data).
  const clientPropertyIds = useMemo(() => new Set(properties.map((p) => p.id)), [properties]);
  const clientParcels = useMemo(
    () => (parcelsQuery.data ?? []).filter((p) => p.property_id != null && clientPropertyIds.has(p.property_id)),
    [parcelsQuery.data, clientPropertyIds],
  );

  const events = eventsQuery.data ?? [];
  const today = todayIso();
  const upcoming = useMemo(
    () => events.filter((e) => e.event_date >= today)
      .sort((a, b) => (a.event_date === b.event_date
        ? (a.event_time ?? '').localeCompare(b.event_time ?? '')
        : a.event_date.localeCompare(b.event_date))),
    [events, today],
  );
  const past = useMemo(
    () => events.filter((e) => e.event_date < today)
      .sort((a, b) => (a.event_date === b.event_date
        ? (b.event_time ?? '').localeCompare(a.event_time ?? '')
        : b.event_date.localeCompare(a.event_date))),
    [events, today],
  );

  if (!canView) {
    return <Empty message="You do not have permission to view this client's calendar." />;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
          <StatCard label="Upcoming" value={String(upcoming.length)} caption="on this client" accent="hunter" />
          <StatCard label="Past" value={String(past.length)} caption="recorded" accent="info" />
        </div>
        {canAdd && <Button variant="primary" size="sm" onClick={() => setCreating(true)}>+ New event</Button>}
      </div>

      {eventsQuery.isLoading && <p style={{ color: 'var(--color-ink-3)' }}>Loading events…</p>}
      {eventsQuery.error && (
        <div style={{ padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)' }}>
          {(eventsQuery.error as Error).message}
        </div>
      )}
      {!eventsQuery.isLoading && !eventsQuery.error && events.length === 0 && (
        <Empty message="No events on this client yet. Use “+ New event” to schedule a deadline, hearing, tax-bill date, or reminder." />
      )}

      {upcoming.length > 0 && (
        <ClientEventSection title="Upcoming" events={upcoming} users={usersQuery.data ?? []} onOpen={setEditing} />
      )}
      {past.length > 0 && (
        <ClientEventSection title="Past" events={past} users={usersQuery.data ?? []} onOpen={setEditing} />
      )}

      {(creating || editing) && (
        <EventModal
          key={editing?.id ?? 'new'}
          event={editing}
          defaultDate={todayIso()}
          users={usersQuery.data ?? []}
          parcels={clientParcels}
          clients={[]}
          properties={properties}
          lockClient={{ id: clientId, name: clientName }}
          canEdit={editing ? canEdit : canAdd}
          canDelete={canDelete}
          saving={createMut.isPending || updateMut.isPending}
          deleting={deleteMut.isPending}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={(body) => {
            if (editing) updateMut.mutate({ id: editing.id, body });
            else createMut.mutate(body);
          }}
          onDelete={editing ? () => deleteMut.mutate(editing.id) : undefined}
        />
      )}
    </>
  );
}

/** A titled list of client events; each row opens the shared EventModal. */
function ClientEventSection({
  title, events, users, onOpen,
}: {
  title: string;
  events: CalendarEvent[];
  users: UserLite[];
  onOpen: (e: CalendarEvent) => void;
}) {
  return (
    <section style={{ marginBottom: 'var(--sp-5)' }}>
      <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{title}</h3>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {events.map((e) => (
          <li key={e.id}>
            <button
              onClick={() => onOpen(e)}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                padding: 'var(--sp-2) var(--sp-3)', borderLeft: `3px solid ${metaFor(e.event_type).accent}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>
                  {eventTitle(e)}
                </span>
                <Badge variant={metaFor(e.event_type).variant}>{metaFor(e.event_type).label}</Badge>
              </div>
              <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                {formatEventDate(e.event_date)}{e.event_time ? ` · ${formatEventTime(e.event_time)}` : ''}{e.location ? ` · ${e.location}` : ''}
                {e.assigned_user_id ? ` · ${userLabel(users, e.assigned_user_id)}` : ''}
              </div>
              {/* Linked property/parcel (client is implicit on this tab). */}
              <EventLinkage event={{ ...e, client_name: null }} style={{ marginTop: 'var(--sp-1)' }} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Domain 18: Onboarding, Documents & Contracts ───────────────────────────

interface OnboardingItem {
  document_type: string;
  cadence: 'one_time' | 'annual' | null;
  required: boolean;
  is_prerequisite: boolean;
  satisfied: boolean;
  satisfying_document_id: string | null;
  year: number | null;
}
interface OnboardingStatus {
  complete: boolean;
  prerequisites_met: boolean;
  items: OnboardingItem[];
}
interface Contract {
  id: string;
  name: string;
  document_type: string | null;
  cadence: 'one_time' | 'annual' | null;
  year: number | null;
  file_size_bytes: number | null;
  created_at: string;
  uploaded_by_user_id: string | null;
}
interface ClientDocument {
  id: string;
  name: string;
  document_type: string | null;
  filename: string;
  file_size_bytes: number | null;
  year: number;
  cadence: 'one_time' | 'annual' | null;
  property_id: string | null;
  status: string | null;
  notify_client?: number | boolean | null;
  created_at: string;
}

const fmtBytes = (v: number | null | undefined) => {
  if (v == null) return '—';
  if (v < 1024) return `${v}B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)}KB`;
  return `${(v / 1024 / 1024).toFixed(1)}MB`;
};

/**
 * Shared onboarding-status query — banner + checklist panel dedupe on this key.
 * Namespaced under ['documents', …] so an upload via DocumentUploadModal (which
 * invalidates ['documents']) re-derives satisfaction without any manual wiring.
 */
function useOnboardingStatus(clientId: string) {
  return useQuery<OnboardingStatus>({
    queryKey: ['documents', 'onboarding', clientId],
    queryFn: ({ signal }) => api.get<OnboardingStatus>(`/api/clients/${clientId}/onboarding`, signal),
    enabled: !!clientId,
  });
}

/**
 * OnboardingGateBanner — the Appointment-of-Agents (AoA) hard gate. Renders a
 * prominent warning under the header whenever a `is_prerequisite` requirement is
 * unmet, mirroring the server-side assertFilingPrerequisites 409 that blocks
 * file-appeal. Silent when the gate is satisfied or not configured (empty items).
 */
function OnboardingGateBanner({ clientId, onReview }: { clientId: string; onReview: () => void }) {
  const { data } = useOnboardingStatus(clientId);
  // Only render once we know the gate is unmet — no flash while loading, no
  // banner when there is no prerequisite configured (prerequisites_met true).
  if (!data || data.prerequisites_met) return null;
  const missing = data.items.filter((i) => i.is_prerequisite && !i.satisfied);
  if (missing.length === 0) return null;

  return (
    <div
      role="alert"
      style={{
        marginTop: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
        display: 'flex',
        gap: 'var(--sp-3)',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 320px' }}>
        <div style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>
          Authorization of Agent required before filing
        </div>
        <p style={{ margin: 'var(--sp-1) 0 0', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)' }}>
          No appeal can be filed for this client until every prerequisite document is on file. Missing:{' '}
          {missing.map((m) => m.document_type).join(', ')}.
        </p>
      </div>
      <Button variant="secondary" size="sm" onClick={onReview}>Review documents</Button>
    </div>
  );
}

/** One onboarding requirement row: status pill + type + cadence/year context. */
function ChecklistRow({ item }: { item: OnboardingItem }) {
  const { toast } = useToast();
  let pill: { variant: 'success' | 'danger' | 'warning' | 'neutral'; label: string };
  if (item.satisfied) pill = { variant: 'success', label: 'On file' };
  else if (item.is_prerequisite) pill = { variant: 'danger', label: 'Blocks filing' };
  else if (item.required) pill = { variant: 'warning', label: 'Missing' };
  else pill = { variant: 'neutral', label: 'Optional' };

  const context = [
    item.cadence === 'annual' ? `Annual${item.year ? ` · ${item.year}` : ''}` : item.cadence === 'one_time' ? 'One-time' : null,
    item.is_prerequisite ? 'Prerequisite' : null,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)',
      padding: 'var(--sp-2) 0', borderBottom: '1px solid var(--color-border)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>
          {humanizeToken(item.document_type)}
        </div>
        {context && (
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginTop: 2 }}>{context}</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexShrink: 0 }}>
        {item.satisfying_document_id && (
          <Button variant="ghost" size="sm" onClick={() => downloadDocument(item.satisfying_document_id!, (m) => toast({ variant: 'error', message: m }))}>
            Download
          </Button>
        )}
        <Badge variant={pill.variant} dot>{pill.label}</Badge>
      </div>
    </div>
  );
}

/** OnboardingChecklistPanel — the standardized checklist with live satisfaction. */
function OnboardingChecklistPanel({ clientId }: { clientId: string }) {
  const query = useOnboardingStatus(clientId);

  if (query.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading onboarding checklist…</p>;
  if (query.error) {
    const denied = query.error instanceof ApiClientError && query.error.status === 403;
    if (denied) return null; // no permission to see onboarding — hide the panel entirely
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{(query.error as Error).message}</div>
    );
  }
  const status = query.data;
  if (!status || status.items.length === 0) {
    return <Empty message="No onboarding checklist is configured for this organization yet." />;
  }
  const satisfied = status.items.filter((i) => i.satisfied).length;

  return (
    <InfoCard label="Onboarding checklist">
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', marginBottom: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <Badge variant={status.complete ? 'success' : 'warning'} dot>
          {status.complete ? 'Complete' : `${satisfied}/${status.items.length} on file`}
        </Badge>
        <Badge variant={status.prerequisites_met ? 'success' : 'danger'} dot>
          {status.prerequisites_met ? 'Filing unlocked' : 'Filing blocked (AoA)'}
        </Badge>
      </div>
      {status.items.map((item) => (
        <ChecklistRow key={`${item.document_type}-${item.year ?? 'any'}`} item={item} />
      ))}
    </InfoCard>
  );
}

/**
 * DocumentsTab — Domain 18.1/18.2 on the client detail. Three real surfaces over
 * the shipped backend: the onboarding checklist (GET /clients/:id/onboarding), the
 * client-level contract archive across every year (GET /clients/:id/contracts), and
 * all documents on file (GET /api/documents?client_id=). Upload reuses the shared
 * DocumentUploadModal pre-linked to this client.
 */
const DOCS_PAGE_SIZE = 20;

function DocumentsTab({ clientId, properties, initialShared = false, onSharedConsumed }: { clientId: string; properties: Property[]; initialShared?: boolean; onSharedConsumed?: () => void }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  // D2 — the share-control is a document mutation server-side; mirror that on the
  // UI so read-only staff don't see a dead toggle.
  const canShare = useEntityPermission('document', 'can_edit');
  // Documents are housed on the client but belong to a property — map the doc's
  // property_id to its name for the All-Documents "Property" column.
  const propsById = useMemo(() => new Map(properties.map(p => [p.id, p.name])), [properties]);

  // Client-side search / type-filter / pagination over the already-fetched client
  // documents (the endpoint returns every doc linked to the client). Mirrors the
  // list-page toolbar+pager pattern; a filter or search change resets to page 1.
  const [docSearch, setDocSearch] = useState('');
  const [docType, setDocType] = useState('');
  // D2 — portal-share filter (all / shared / not shared). Seeded from a `shared=1`
  // deep link so the Client Portal section's "shared documents" list can jump here
  // pre-filtered to what the client can see (one list, linked — not a second copy).
  const [docShared, setDocShared] = useState<'' | 'shared' | 'unshared'>(
    () => (initialShared || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('shared') === '1') ? 'shared' : ''),
  );
  // The initialShared prop is a ONE-SHOT jump signal from the Client Portal
  // section. Clear it in the parent once consumed so a later tab switch (which
  // remounts this tab and re-runs the state initializer) doesn't stickily
  // re-force the "shared" filter and lose the user's manual filter choice.
  useEffect(() => {
    if (initialShared) onSharedConsumed?.();
    // Run once on mount — the prop is only meaningful at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [docPage, setDocPage] = useState(0); // 0-based page index

  // D2 — toggle a document's portal share (PATCH /api/documents/:id/share). The
  // server flips notify_client + notifies the client's portal on newly-shared;
  // invalidating ['documents'] refreshes both this list and the portal section.
  const shareMut = useMutation({
    mutationFn: ({ id, share }: { id: string; share: boolean }) =>
      api.patch(`/api/documents/${id}/share`, { notify_client: share }),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({ variant: 'success', message: vars.share ? 'Shared to the client portal.' : 'Removed from the client portal.' });
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });
  const isShared = (d: ClientDocument) => d.notify_client === 1 || d.notify_client === true;

  // B3 — multi-select share. Select several documents (checkbox column) and share
  // or unshare them to the portal in one action, instead of one row at a time.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelected = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkShareMut = useMutation({
    mutationFn: async ({ ids, share }: { ids: string[]; share: boolean }) => {
      await Promise.all(ids.map((id) => api.patch(`/api/documents/${id}/share`, { notify_client: share })));
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setSelected(new Set());
      toast({ variant: 'success', message: `${vars.ids.length} document${vars.ids.length === 1 ? '' : 's'} ${vars.share ? 'shared to' : 'removed from'} the client portal.` });
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  // Keys namespaced under ['documents', …] so the shared DocumentUploadModal's
  // onSuccess invalidation of ['documents'] refreshes both lists automatically.
  const contractsQuery = useQuery<Contract[]>({
    queryKey: ['documents', 'contracts', clientId],
    queryFn: ({ signal }) => api.get<Contract[]>(`/api/clients/${clientId}/contracts`, signal),
    enabled: !!clientId,
  });
  const docsQuery = useQuery<ClientDocument[]>({
    queryKey: ['documents', 'client', clientId],
    queryFn: ({ signal }) => api.get<ClientDocument[]>(`/api/documents?client_id=${clientId}`, signal),
    enabled: !!clientId,
  });

  const contractColumns = useMemo<ColumnDef<Contract>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => {
      const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—';
    } },
    { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'file_size_bytes', header: 'Size', cell: ({ getValue }) => fmtBytes(getValue() as number | null) },
    { accessorKey: 'created_at', header: 'Uploaded', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { id: 'download', header: '', cell: ({ row }) => (
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m })); }}>Download</Button>
    ) },
  ], [toast]);

  const docColumns = useMemo<ColumnDef<ClientDocument>[]>(() => [
    // B3 — per-row select checkbox for multi-select share (only when the user can share).
    ...(canShare ? [{
      id: 'select',
      header: '',
      cell: ({ row }: { row: { original: ClientDocument } }) => (
        <input
          type="checkbox"
          checked={selected.has(row.original.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleSelected(row.original.id)}
          aria-label={`Select ${row.original.name}`}
          style={{ cursor: 'pointer', width: 16, height: 16, accentColor: 'var(--brand-primary)' }}
        />
      ),
    } as ColumnDef<ClientDocument>] : []),
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => {
      const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—';
    } },
    { accessorKey: 'property_id', header: 'Property', cell: ({ getValue }) => {
      const pid = getValue() as string | null;
      if (!pid) return <span style={{ color: 'var(--color-muted)' }}>—</span>;
      const name = propsById.get(pid);
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/properties/${pid}`); }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'inherit' }}
        >{name || 'Property'}</button>
      );
    } },
    { accessorKey: 'year', header: 'Year' },
    { accessorKey: 'file_size_bytes', header: 'Size', cell: ({ getValue }) => fmtBytes(getValue() as number | null) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge> },
    // D2 — portal share status + toggle. "Shared" links this doc into the client
    // portal (one copy, listed there — not a second file).
    { id: 'portal', header: 'Portal', cell: ({ row }) => {
      const shared = isShared(row.original);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }} onClick={(e) => e.stopPropagation()}>
          {shared ? <Badge variant="success" dot>Shared</Badge> : <span style={{ color: 'var(--color-muted)' }}>Private</span>}
          {canShare && (
            <Button
              variant="ghost"
              size="sm"
              loading={shareMut.isPending && shareMut.variables?.id === row.original.id}
              onClick={(e) => { e.stopPropagation(); shareMut.mutate({ id: row.original.id, share: !shared }); }}
            >{shared ? 'Unshare' : 'Share'}</Button>
          )}
        </div>
      );
    } },
    { id: 'download', header: '', cell: ({ row }) => (
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m })); }}>Download</Button>
    ) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [propsById, navigate, toast, canShare, shareMut.isPending, shareMut.variables?.id, selected]);

  const contracts = contractsQuery.data ?? [];
  const docs = docsQuery.data ?? [];

  // Distinct document types present on this client → the type-filter Select.
  const docTypeOptions = useMemo<SelectOption[]>(() => {
    const seen = new Set<string>();
    for (const d of docs) if (d.document_type) seen.add(d.document_type);
    return [...seen].sort().map((t) => ({ value: t, label: t }));
  }, [docs]);

  // Search matches name + type substring; type + portal-share filters are exact.
  const filteredDocs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    return docs.filter((d) => {
      if (docType && (d.document_type ?? '') !== docType) return false;
      if (docShared === 'shared' && !isShared(d)) return false;
      if (docShared === 'unshared' && isShared(d)) return false;
      if (!q) return true;
      return `${d.name ?? ''} ${d.document_type ?? ''}`.toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, docSearch, docType, docShared]);

  const docPageCount = Math.max(1, Math.ceil(filteredDocs.length / DOCS_PAGE_SIZE));
  // Clamp the page so shrinking the result set (via search/filter) never lands on
  // an out-of-range empty page even before the reset handlers run.
  const docSafePage = Math.min(docPage, docPageCount - 1);
  const pagedDocs = useMemo(
    () => filteredDocs.slice(docSafePage * DOCS_PAGE_SIZE, docSafePage * DOCS_PAGE_SIZE + DOCS_PAGE_SIZE),
    [filteredDocs, docSafePage],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>Upload document</Button>
      </div>

      <OnboardingChecklistPanel clientId={clientId} />

      <section>
        <SectionHeading title="Contracts" caption="Every engagement contract on file, across all years." />
        {contractsQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)' }}>Loading contracts…</p>
        ) : contractsQuery.error ? (
          <ErrorBox error={contractsQuery.error as Error} />
        ) : contracts.length === 0 ? (
          <Empty message="No contracts on file for this client yet." />
        ) : (
          <DataTable data={contracts} columns={contractColumns} onRowClick={(c) => navigate(`/documents/${c.id}`)} />
        )}
      </section>

      <section>
        <SectionHeading title="All documents" caption="Every document linked to this client." />
        {docsQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)' }}>Loading documents…</p>
        ) : docsQuery.error ? (
          <ErrorBox error={docsQuery.error as Error} />
        ) : docs.length === 0 ? (
          <Empty message="No documents on this client yet. Use “Upload document” to add one." />
        ) : (
          <>
            <ListToolbar
              searchValue={docSearch}
              onSearchChange={(v) => { setDocSearch(v); setDocPage(0); }}
              searchPlaceholder="Search documents by name or type…"
              filters={[
                {
                  key: 'document_type',
                  value: docType,
                  onChange: (v) => { setDocType(v); setDocPage(0); },
                  placeholder: 'All types',
                  options: docTypeOptions,
                  widthPx: 200,
                },
                {
                  key: 'shared',
                  value: docShared,
                  onChange: (v) => { setDocShared(v as '' | 'shared' | 'unshared'); setDocPage(0); },
                  placeholder: 'All (portal)',
                  options: [{ value: 'shared', label: 'Shared to portal' }, { value: 'unshared', label: 'Not shared' }],
                  widthPx: 180,
                },
              ]}
            />
            {/* B3 — bulk share bar: appears when documents are selected. */}
            {canShare && selected.size > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap',
                padding: 'var(--sp-2) var(--sp-3)', marginBottom: 'var(--sp-3)',
                background: 'var(--brand-primary-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
              }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}>{selected.size} selected</span>
                <Button variant="primary" size="sm" loading={bulkShareMut.isPending} onClick={() => bulkShareMut.mutate({ ids: [...selected], share: true })}>Share to portal</Button>
                <Button variant="secondary" size="sm" loading={bulkShareMut.isPending} onClick={() => bulkShareMut.mutate({ ids: [...selected], share: false })}>Remove from portal</Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
              </div>
            )}
            <DataTable
              data={pagedDocs}
              columns={docColumns}
              onRowClick={(d) => navigate(`/documents/${d.id}`)}
              emptyMessage="No documents match your search."
            />
            {filteredDocs.length > DOCS_PAGE_SIZE && (
              <Pagination
                pageIndex={docSafePage}
                pageCount={docPageCount}
                pageSize={DOCS_PAGE_SIZE}
                totalRows={filteredDocs.length}
                onPageChange={setDocPage}
              />
            )}
          </>
        )}
      </section>

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultContext={{ client_id: clientId }}
      />
    </div>
  );
}

function SectionHeading({ title, caption }: { title: string; caption?: string }) {
  return (
    <div style={{ marginBottom: 'var(--sp-3)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{title}</h3>
      {caption && <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{caption}</p>}
    </div>
  );
}

function ErrorBox({ error }: { error: Error }) {
  return (
    <div style={{
      padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
      border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
      borderRadius: 'var(--r-lg)',
    }}>{error.message}</div>
  );
}

/**
 * PortalAccessTab (09.1) — staff manage WHICH properties each portal (client-role)
 * user of this client can see. Reads GET /api/clients/:id/portal-users (the client's
 * portal users, each with their resolved grant set) and mutates via
 * POST/DELETE /api/clients/:id/portal-grants. All real, org- + book-scoped server-side.
 *
 * A grant WIDENS what a portal user sees:
 *   - "Entire client" (client-scope on this client) → all of the client's properties.
 *   - a specific property (property-scope) → restricts a contact to just those properties.
 * With no grants a portal user falls back to their primary client (all its properties).
 */
interface PortalGrant {
  id: string;
  portal_user_id: string;
  scope_type: 'client' | 'property';
  scope_id: string;
  scope_label: string | null;
  created_at: string;
}
interface PortalUser {
  id: string;
  email: string;
  client_id: string | null;
  first_name: string | null;
  last_name: string | null;
  grants: PortalGrant[];
}

function PortalAccessTab({ clientId, clientName, properties, onOpenSharedDocuments }: { clientId: string; clientName: string; properties: Property[]; onOpenSharedDocuments?: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Grants are a client mutation server-side (checkPermission 'client' PUT); mirror
  // that on the UI so read-only staff don't see dead controls.
  const canManage = useEntityPermission('client', 'can_edit');

  const usersQuery = useQuery<PortalUser[]>({
    queryKey: ['portal-users', clientId],
    queryFn: ({ signal }) => api.get<PortalUser[]>(`/api/clients/${clientId}/portal-users`, signal),
    enabled: !!clientId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['portal-users', clientId] });

  const grantMut = useMutation({
    mutationFn: (body: { portal_user_id: string; scope_type: 'client' | 'property'; scope_id: string }) =>
      api.post<PortalGrant>(`/api/clients/${clientId}/portal-grants`, body),
    onSuccess: () => { toast({ variant: 'success', message: 'Access granted.' }); invalidate(); },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  const revokeMut = useMutation({
    mutationFn: (grantId: string) => api.delete(`/api/clients/${clientId}/portal-grants/${grantId}`),
    onSuccess: () => { toast({ variant: 'success', message: 'Access revoked.' }); invalidate(); },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  if (usersQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading portal users…</p>;
  if (usersQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{(usersQuery.error as Error).message}</div>
    );
  }

  const users = usersQuery.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      {/* D1 — Portal Access block: add a client contact to the portal + invite. */}
      <PortalInviteBlock clientId={clientId} canManage={canManage} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <SectionHeading title="Portal users" caption="Client-role users of this client. Control which properties each one sees." />
        {users.length === 0 ? (
          <Empty message={`No portal users are set up for ${clientName} yet. Use “Invite to portal” above to add one.`} />
        ) : (
          <>
            <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              Control which properties each portal user sees. Grant the entire client, or restrict a
              contact to specific properties. With no grants, a user sees all of their primary client's properties.
            </p>
            {users.map((u) => (
              <PortalUserCard
                key={u.id}
                user={u}
                clientId={clientId}
                properties={properties}
                canManage={canManage}
                onGrant={(scope_type, scope_id) => grantMut.mutate({ portal_user_id: u.id, scope_type, scope_id })}
                onRevoke={(grantId) => revokeMut.mutate(grantId)}
                granting={grantMut.isPending}
                revoking={revokeMut.isPending}
              />
            ))}
          </>
        )}
      </div>

      {/* D2 — documents shared to the portal, listed (not housed): each links back
          to the All Documents section filtered to shared (one copy, one place). */}
      <PortalSharedDocuments clientId={clientId} onViewAll={onOpenSharedDocuments} />

      <PortalCommentsPanel clientId={clientId} clientName={clientName} canPost={canManage} />
    </div>
  );
}

/**
 * PortalInviteBlock (D1) — the "Portal Access" block at the top of the Client
 * Portal section: pick one of the client's contacts (across client / property /
 * parcel level, labeled the same way as the Contacts tab so the inviter can see
 * what each contact is attached to) and invite them to the portal. Invites go
 * through POST /api/clients/:id/portal-invite, which creates a role='client' user
 * bound to this client with a single-use invite token.
 */
interface GroupedInviteContact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  record_label?: string | null;
  property_label?: string | null;
}
function PortalInviteBlock({ clientId, canManage }: { clientId: string; canManage: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [contactId, setContactId] = useState<string | null>(null);

  const q = useQuery<{ client: GroupedInviteContact[]; property: GroupedInviteContact[]; parcel: GroupedInviteContact[] }>({
    queryKey: ['client-contacts-grouped', clientId],
    queryFn: ({ signal }) => api.get(`/api/client-contacts-grouped/${clientId}`, signal),
    enabled: !!clientId && canManage,
  });

  // Build the invite options across all three levels, labeled like the Contacts
  // tab: "Name — Client level" / "— Property: X" / "— Parcel: Y (Property)".
  const options = useMemo(() => {
    const name = (c: GroupedInviteContact) => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name || 'Unnamed contact';
    const out: { value: string; label: string }[] = [];
    const seen = new Set<string>();
    const push = (c: GroupedInviteContact, where: string) => {
      if (seen.has(c.id)) return; // one entry per contact even if attached at several spots
      seen.add(c.id);
      out.push({ value: c.id, label: `${name(c)} — ${where}` });
    };
    (q.data?.client ?? []).forEach((c) => push(c, 'Client level'));
    (q.data?.property ?? []).forEach((c) => push(c, `Property: ${c.record_label ?? '—'}`));
    (q.data?.parcel ?? []).forEach((c) => push(c, `Parcel: ${c.record_label ?? '—'}${c.property_label ? ` (${c.property_label})` : ''}`));
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data]);

  const inviteMut = useMutation({
    mutationFn: (cid: string) => api.post<{ reinvited?: boolean; alreadyActive?: boolean }>(`/api/clients/${clientId}/portal-invite`, { contact_id: cid }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['portal-users', clientId] });
      setContactId(null);
      const message = res?.alreadyActive
        ? 'This contact already has active portal access.'
        : res?.reinvited
          ? 'Invite re-sent — the contact can set their password again.'
          : 'Invited — the contact has been added to the portal.';
      toast({ variant: res?.alreadyActive ? 'info' : 'success', message });
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  if (!canManage) return null;

  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <SectionHeading title="Portal access" caption="Add a contact to this client's portal and send them an invite. Contacts are listed across client, property, and parcel level." />
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 'var(--sp-3)' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-1)' }}>Contact to invite</label>
          <SearchableSelect
            options={options}
            value={contactId}
            onChange={setContactId}
            placeholder={q.isLoading ? 'Loading contacts…' : options.length ? 'Search this client’s contacts…' : 'No contacts on this client yet'}
          />
        </div>
        <Button
          variant="primary"
          disabled={!contactId}
          loading={inviteMut.isPending}
          onClick={() => contactId && inviteMut.mutate(contactId)}
        >Invite to portal</Button>
      </div>
    </div>
  );
}

/**
 * PortalSharedDocuments (D2) — the Client Portal section's list of documents
 * shared to the portal. LISTED, not housed: each entry links to the document, and
 * a "View in All Documents" jump opens the Documents tab pre-filtered to shared,
 * so there is exactly one copy of a document, in one place.
 */
function PortalSharedDocuments({ clientId, onViewAll }: { clientId: string; onViewAll?: () => void }) {
  const navigate = useNavigate();
  const q = useQuery<ClientDocument[]>({
    queryKey: ['documents', 'client', clientId],
    queryFn: ({ signal }) => api.get<ClientDocument[]>(`/api/documents?client_id=${clientId}`, signal),
    enabled: !!clientId,
  });
  const shared = (q.data ?? []).filter((d) => d.notify_client === 1 || d.notify_client === true);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <SectionHeading title="Documents shared to the portal" caption="What this client can see in their portal. Listed here — managed in All Documents." />
        {onViewAll && (
          <Button variant="ghost" size="sm" onClick={onViewAll}>View in All Documents →</Button>
        )}
      </div>
      {q.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>
      ) : shared.length === 0 ? (
        <Empty message="No documents are shared to this client's portal yet. Share one from the Documents & Contracts tab." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)' }}>
          {shared.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => navigate(`/documents/${d.id}`)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', textAlign: 'left',
                padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-card)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--r-md)', cursor: 'pointer', color: 'var(--color-ink)', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
              }}
            >
              <span style={{ fontWeight: 'var(--fw-medium)', overflowWrap: 'anywhere' }}>{d.name}</span>
              <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center', flexShrink: 0 }}>
                {d.document_type && <Badge variant="neutral">{humanizeToken(d.document_type)}</Badge>}
                <Badge variant="success" dot>Shared</Badge>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Portal conversation (09.4) — the firm's side of the client ⇆ agent thread ──
// Reads/writes the SAME thread the client sees in their portal
// (/api/clients/:id/portal-comments → notes entity_type='client_portal'). A firm
// reply lands in the client's portal AND emails them; threaded via parent_id.
interface PortalComment {
  id: string; content: string; created_at: string; parent_id: string | null;
  is_self: boolean; author_type: 'client' | 'firm'; author_name: string;
}
interface PortalCommentNode extends PortalComment { replies: PortalCommentNode[] }

function threadPortalComments(rows: PortalComment[]): PortalCommentNode[] {
  const byId = new Map<string, PortalCommentNode>();
  rows.forEach((r) => byId.set(r.id, { ...r, replies: [] }));
  const roots: PortalCommentNode[] = [];
  rows.forEach((r) => {
    const node = byId.get(r.id)!;
    const parent = r.parent_id ? byId.get(r.parent_id) : undefined;
    if (parent) parent.replies.push(node);
    else roots.push(node);
  });
  return roots;
}

function PortalCommentsPanel({ clientId, clientName, canPost }: { clientId: string; clientName: string; canPost: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');

  const q = useQuery<PortalComment[]>({
    queryKey: ['portal-comments', clientId],
    queryFn: ({ signal }) => api.get<PortalComment[]>(`/api/clients/${clientId}/portal-comments`, signal),
    enabled: !!clientId,
  });

  const mut = useMutation({
    mutationFn: (body: { content: string; parent_id?: string }) =>
      api.post(`/api/clients/${clientId}/portal-comments`, body),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['portal-comments', clientId] });
      if (vars.parent_id) { setReplyText(''); setReplyingId(null); } else { setText(''); }
      toast({ variant: 'success', message: 'Sent — the client has been notified.' });
    },
    onError: (e) => {
      const body = e instanceof ApiClientError ? (e.body as { error?: string }) : undefined;
      toast({ variant: 'error', message: body?.error || (e as Error).message });
    },
  });

  const rows = q.data ?? [];
  const threads = useMemo(() => threadPortalComments(rows), [rows]);

  const submitReply = (parentId: string) => {
    if (!replyText.trim()) return;
    mut.mutate({ content: replyText.trim(), parent_id: parentId });
  };

  const renderComposer = (value: string, onChange: (v: string) => void, onSubmit: () => void, label: string, placeholder: string, pending: boolean) => (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{
          width: '100%', boxSizing: 'border-box', padding: 'var(--sp-2) var(--sp-3)',
          fontSize: 'var(--fs-sm)', borderRadius: 'var(--r-md)', border: '1px solid var(--color-border)',
          resize: 'vertical', fontFamily: 'inherit', background: 'var(--color-input, var(--color-card))', color: 'var(--color-ink)',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-2)' }}>
        <Button size="sm" variant="primary" disabled={!value.trim() || pending} onClick={onSubmit}>{label}</Button>
      </div>
    </div>
  );

  const renderNode = (node: PortalCommentNode) => {
    const isReplying = replyingId === node.id;
    return (
      <li key={node.id} style={{ padding: 'var(--sp-3) 0', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-1)' }}>
          <Badge variant={node.author_type === 'client' ? 'info' : 'neutral'}>
            {node.author_type === 'client' ? node.author_name : (node.is_self ? 'You' : node.author_name)}
          </Badge>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{formatDateTime(node.created_at)}</span>
        </div>
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', whiteSpace: 'pre-wrap' }}>{node.content}</div>
        {canPost && (
          <div style={{ marginTop: 'var(--sp-1)' }}>
            <button
              type="button"
              onClick={() => { setReplyingId(isReplying ? null : node.id); setReplyText(''); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)' }}
            >{isReplying ? 'Cancel' : 'Reply'}</button>
          </div>
        )}
        {isReplying && (
          <div style={{ marginTop: 'var(--sp-2)' }}>
            {renderComposer(replyText, setReplyText, () => submitReply(node.id), 'Post reply', 'Write a reply to the client…', mut.isPending && !!replyingId)}
          </div>
        )}
        {node.replies.length > 0 && (
          <ul style={{ listStyle: 'none', padding: '0 0 0 var(--sp-3)', margin: 'var(--sp-1) 0 0', borderLeft: '2px solid var(--color-border)' }}>
            {node.replies.map(renderNode)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <h3 style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-md)', color: 'var(--color-ink)' }}>Portal conversation</h3>
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        The shared thread {clientName}'s portal users see. Posting here appears in their portal and emails them.
      </p>

      {canPost && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          {renderComposer(text, setText, () => mut.mutate({ content: text.trim() }), 'Post to portal', `Write a message to ${clientName}…`, mut.isPending && !replyingId)}
        </div>
      )}

      {q.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading conversation…</p>
      ) : q.error ? (
        <p style={{ color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>{(q.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>No portal messages yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>{threads.map(renderNode)}</ul>
      )}
    </div>
  );
}

function PortalUserCard({
  user, clientId, properties, canManage, onGrant, onRevoke, granting, revoking,
}: {
  user: PortalUser;
  clientId: string;
  properties: Property[];
  canManage: boolean;
  onGrant: (scopeType: 'client' | 'property', scopeId: string) => void;
  onRevoke: (grantId: string) => void;
  granting: boolean;
  revoking: boolean;
}) {
  // Scope options: "Entire client" (client-scope on this client) plus every property
  // of this client. Options already granted are hidden so a staffer can't re-add them.
  const grantedKeys = new Set(user.grants.map((g) => `${g.scope_type}:${g.scope_id}`));
  const options: SelectOption[] = [
    ...(grantedKeys.has(`client:${clientId}`) ? [] : [{ value: `client:${clientId}`, label: 'Entire client (all properties)' }]),
    ...properties
      .filter((p) => !grantedKeys.has(`property:${p.id}`))
      .map((p) => ({ value: `property:${p.id}`, label: `Property: ${p.name}` })),
  ];
  const [selected, setSelected] = useState('');
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();

  const submit = () => {
    if (!selected) return;
    const [scopeType, scopeId] = selected.split(/:(.+)/) as [string, string];
    onGrant(scopeType as 'client' | 'property', scopeId);
    setSelected('');
  };

  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{name || user.email}</div>
          {name && <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{user.email}</div>}
        </div>
        <Badge variant={user.grants.length > 0 ? 'info' : 'neutral'}>
          {user.grants.length === 0 ? 'Default (primary client)' : `${user.grants.length} grant${user.grants.length === 1 ? '' : 's'}`}
        </Badge>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', margin: 'var(--sp-3) 0 0' }}>
        {user.grants.length === 0 ? (
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-muted)' }}>
            No explicit grants — sees all properties of their primary client.
          </span>
        ) : (
          user.grants.map((g) => (
            <span key={g.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)',
              background: 'var(--color-bg)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-pill)', padding: '2px var(--sp-2)', fontSize: 'var(--fs-sm)',
            }}>
              <span style={{ color: 'var(--color-ink-3)' }}>{g.scope_type === 'client' ? 'Client' : 'Property'}:</span>
              <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)' }}>{g.scope_label || g.scope_id}</span>
              {canManage && (
                <button
                  type="button"
                  aria-label={`Revoke access to ${g.scope_label || g.scope_id}`}
                  disabled={revoking}
                  onClick={() => onRevoke(g.id)}
                  style={{
                    border: 'none', background: 'transparent', cursor: revoking ? 'default' : 'pointer',
                    color: 'var(--color-status-red)', fontSize: 'var(--fs-md)', lineHeight: 1, padding: 0,
                  }}
                >×</button>
              )}
            </span>
          ))
        )}
      </div>

      {canManage && options.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', maxWidth: 520 }}>
          <Select
            options={options}
            placeholder="Grant access to…"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            size="sm"
          />
          <Button variant="secondary" size="sm" disabled={!selected} loading={granting} onClick={submit}>
            Grant
          </Button>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
